import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { api } from "@/lib/http";
import { cn } from "@/lib/utils";
import DetectedPercentages from "@/components/text-analyze/DetectedPercentages";
// import { humanizeText, type HumanizeResponse } from "@/lib/textAnalyze";

export const Route = createFileRoute("/_app/seo-scanner")({
  component: RouteComponent,
  staticData: {
    title: "AI Content Website Scanner",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Analyze your website for AI-generated content and get actionable SEO insights",
      },
    ],
  }),
});

const websiteFormSchema = z.object({
  url: z
    .string()
    .min(1, "Please enter a URL")
    .refine((url) => {
      // Allow URLs with or without protocol
      const urlWithProtocol =
        url.startsWith("http://") || url.startsWith("https://")
          ? url
          : `https://${url}`;
      try {
        new URL(urlWithProtocol);
        return true;
      } catch {
        return false;
      }
    }, "Please enter a valid URL (e.g., example.com or https://example.com)"),
  scanName: z.string().optional(),
});

type WebsiteFormData = z.infer<typeof websiteFormSchema>;

type SentenceData = {
  sentence: string;
  color: string;
  paragraph_idx: number;
  score: number;
  label: "HUMAN" | "AI" | "ALTERED";
};

type AnalysisResponse = {
  history_id: string;
  result: {
    overall_score: {
      OPEN_AI: number;
      MISTRAL: number;
      CLAUDE: number;
      GEMINI: number;
      GRAMMAR_ENHANCED: number;
      HUMAN: number;
    };
    sentence_data: Array<SentenceData>;
  };
  model_name: string;
  tokens_used: number;
  created_at: string;
};

function RouteComponent() {
  const [results, setResults] = useState<AnalysisResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<WebsiteFormData>({
    resolver: zodResolver(websiteFormSchema),
    defaultValues: {
      url: "",
      scanName: "",
    },
  });

  const analyzeMutation = useMutation({
    mutationFn: async (data: WebsiteFormData) => {
      const response = await api.detector.post(
        "v2/ai-detector/website-analyzer",
        {
          json: {
            url: data.url,
          },
        },
      );

      return response.json<AnalysisResponse>();
    },
    onSuccess: (data) => {
      setResults(data);
      setError(null); // Clear any previous errors
    },
    onError: () => {
      setError("Failed to retrieve or extract text from the provided URL.");
    },
  });

  const onSubmit = (data: WebsiteFormData) => {
    // Ensure URL has https:// protocol
    const urlWithProtocol =
      data.url.startsWith("http://") || data.url.startsWith("https://")
        ? data.url
        : `https://${data.url}`;

    analyzeMutation.mutate({
      ...data,
      url: urlWithProtocol,
    });
  };

  const reset = () => {
    setResults(null);
    setError(null);
    form.reset();
  };

  if (results) {
    return <AnalysisResults results={results} onReset={reset} />;
  }

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      <div className="mb-8 text-center">
        <h1 className="mb-4 text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
          AI Content Website Scanner
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          Analyze your website for AI-generated content and get actionable SEO
          insights
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/20">
            <Search className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Start Your Website Analysis
          </h2>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="text-red-600 dark:text-red-400">
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-red-800 dark:text-red-200">
                    Analysis Failed
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {error}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setError(null)}
                className="h-auto p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">
                    Website URL *
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="example.com"
                      className="h-12 text-base"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter the main URL of the website you want to analyze
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="scanName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">
                    Scan Name (Optional)
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="My Website Analysis"
                      className="h-12 text-base"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Give this scan a custom name for easy reference
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="h-12 w-full"
              disabled={analyzeMutation.isPending}
            >
              {analyzeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Analyzing Website...
                </>
              ) : (
                <>Start Analysis</>
              )}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}

function AnalysisResults({
  results,
  onReset,
}: {
  results: AnalysisResponse;
  onReset: () => void;
}) {
  // const [humanizedContent, setHumanizedContent] = useState<string | null>(null);
  // const [showHumanized, setShowHumanized] = useState(false);

  const groupedSentences = results.result.sentence_data.reduce<
    Array<Array<SentenceData>>
  >((acc, sentence) => {
    const i = sentence.paragraph_idx;
    if (Array.isArray(acc[i])) acc[i].push(sentence);
    else acc[i] = [sentence];
    return acc;
  }, []);

  const overallScore = results.result.overall_score;

  // Extract all text content from sentences for humanization
  // const originalText = results.result.sentence_data
  //   .map((sentence) => sentence.sentence)
  //   .join(" ");

  // const humanizeMutation = useMutation({
  //   mutationFn: humanizeText,
  //   onSuccess: (data: HumanizeResponse) => {
  //     setHumanizedContent(data.results.HUMANIZER.text);
  //     setShowHumanized(true);
  //   },
  //   onError: (error) => {
  //     console.error("Humanization failed:", error);
  //   },
  // });

  // const toggleView = () => {
  //   setShowHumanized(!showHumanized);
  // };

  return (
    <div className="container mx-auto max-w-6xl px-4 py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-white">
            Website Analysis Results
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Analysis completed • {results.tokens_used} tokens used
          </p>
        </div>
        <div className="flex gap-2">
          {/* <Button
            onClick={handleHumanize}
            disabled={humanizeMutation.isPending}
            variant="default"
          >
            {humanizeMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Humanizing...
              </>
            ) : (
              <>Humanize Content</>
            )}
          </Button> */}
          {/* {humanizedContent && (
            <Button onClick={toggleView} variant="outline">
              {showHumanized ? "Show Original" : "Show Humanized"}
            </Button>
          )} */}
          <Button onClick={onReset} variant="outline">
            New Analysis
          </Button>
        </div>
      </div>

      {/* Content Analysis Overview */}
      <DetectedPercentages results={overallScore} />

      {/* Content Legends */}
      <div className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <h3 className="mb-4 text-lg font-semibold">
          Content Highlighting Legend
        </h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded"
              style={{ backgroundColor: "#FB4141" }}
            ></div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              AI Generated
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded"
              style={{ backgroundColor: "#18abff" }}
            ></div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Altered Content
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded"
              style={{ backgroundColor: "#00E396" }}
            ></div>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Human Content
            </span>
          </div>
        </div>
      </div>

      {/* Content Display */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="mb-6 flex items-center justify-between">
          {/* <h3 className="text-lg font-semibold">
            {showHumanized
              ? "Humanized Website Content"
              : "Analyzed Website Content"}
          </h3>
          {showHumanized && (
            <div className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400">
              <Wand2 className="h-4 w-4" />
              <span>Content has been humanized</span>
            </div>
          )} */}
        </div>
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <div className="min-h-[400px] space-y-4">
            {/* {showHumanized && humanizedContent ? (
              <div className="leading-7 whitespace-pre-wrap text-gray-900 dark:text-gray-100">
                {humanizedContent}
              </div>
            ) : (
              
            )} */}
            {groupedSentences.map((paragraph, paragraphIndex) => (
              <p
                key={paragraphIndex}
                className="leading-7 text-gray-900 dark:text-gray-100"
              >
                {paragraph.map((sentenceData, index) => (
                  <span
                    key={`${sentenceData.sentence}-${sentenceData.score}`}
                    className={cn(
                      "rounded px-1 py-0.5 transition-colors duration-200",
                      index !== 0 && "ml-1",
                    )}
                    style={{
                      backgroundColor:
                        sentenceData.label === "AI"
                          ? "#FB414140"
                          : sentenceData.label === "ALTERED"
                            ? "#18abff40"
                            : // redundant comparison for forward compat
                              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                              sentenceData.label === "HUMAN"
                              ? "#00E39640"
                              : "transparent",
                    }}
                    title={`Score: ${sentenceData.score.toFixed(3)} | Label: ${sentenceData.label}`}
                  >
                    {sentenceData.sentence}
                  </span>
                ))}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
